import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  House, UserPlus, SignIn, Question, List as HamburgerIcon, MouseSimple, Globe, ArrowRight,
  FileText, Lock, Rocket,
} from "@phosphor-icons/react";
import useTrackVisit from "@/hooks/useTrackVisit";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";
import GoogleSignInButton from "@/components/auth/GoogleSignInButton";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/55900b9a-a52d-4fb1-bd58-9356b2e1d880/images/02675872ac97cf847101326c537b801fc4ffa02ac3811d167795f9981f744b74.jpeg";
const HEADING_FONT = { fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" };

const NAV = [
  { to: "/", label: "Home", icon: House, testid: "nav-home" },
  { to: "/register", label: "Register", icon: UserPlus, testid: "nav-register" },
  { to: "/login", label: "Login", icon: SignIn, testid: "nav-login" },
  { to: "/support", label: "FAQ & Contact", icon: Question, testid: "nav-faq" },
];

const QUICK = [
  { to: "/register", label: "Join Free", icon: UserPlus, testid: "quick-join" },
  { to: "/login", label: "Member Login", icon: SignIn, testid: "quick-login" },
  { to: "/register", label: "Submit Your Website", icon: Globe, testid: "quick-submit-site" },
  { to: "/register", label: "Start Surfing", icon: MouseSimple, testid: "quick-start-surfing" },
  { to: "/support", label: "How It Works", icon: Rocket, testid: "quick-how-it-works" },
  { to: "/terms", label: "Terms of Service", icon: FileText, testid: "quick-terms" },
  { to: "/privacy", label: "Privacy Policy", icon: Lock, testid: "quick-privacy" },
];

const navPill = ({ isActive }) =>
  `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
    isActive ? "bg-green-600 text-white shadow-sm" : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
  }`;

function MobileMenu({ logo }) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button data-testid="landing-mobile-menu-btn" aria-label="Open navigation"
          className="md:hidden h-10 w-10 -ml-1 inline-flex items-center justify-center rounded-full text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors duration-200">
          <HamburgerIcon size={20} weight="bold" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" data-testid="landing-mobile-menu" className="w-[85%] max-w-xs bg-white dark:bg-slate-900 p-0 flex flex-col">
        <SheetHeader className="px-4 py-4 border-b border-slate-200 dark:border-slate-800 flex-row items-center gap-3 space-y-0">
          <img src={logo} alt="logo" className="h-10" />
          <SheetTitle className="text-base font-black text-left flex-1" style={HEADING_FONT}>Menu</SheetTitle>
        </SheetHeader>
        <nav className="flex-1 overflow-y-auto flex flex-col text-center">
          {[...NAV, ...QUICK.slice(4)].map((l) => (
            <SheetClose asChild key={l.testid}>
              <NavLink to={l.to} end data-testid={`mobile-${l.testid}`}
                className="block w-full px-5 py-4 text-xl font-black tracking-tight border-b border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800">
                {l.label}
              </NavLink>
            </SheetClose>
          ))}
        </nav>
      </SheetContent>
    </Sheet>
  );
}

function Header({ logo, user, googleReady, clientId }) {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-40 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 flex items-center h-16 sm:h-24 gap-2">
        <MobileMenu logo={logo} />
        <Link to="/" className="flex items-center pr-2 sm:pr-4" data-testid="landing-brand-logo">
          <img src={logo} alt="logo" className="h-12 sm:h-20" />
        </Link>
        <nav className="hidden md:flex items-center gap-1 flex-1" data-testid="landing-top-nav">
          {NAV.map((l) => (
            <NavLink key={l.testid} to={l.to} end data-testid={l.testid} className={navPill}>
              <l.icon size={18} weight="bold" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <ThemeToggle />
          {user ? (
            <button data-testid="landing-dashboard-btn" onClick={() => navigate("/dashboard")}
              className="rounded-full bg-green-600 hover:bg-green-500 text-white font-bold px-4 py-2 text-sm transition-colors duration-200">
              My Dashboard
            </button>
          ) : (
            <>
              <div className="hidden lg:block" data-testid="header-google-signin">
                {googleReady && <GoogleSignInButton clientId={clientId} mode="signin_with" />}
              </div>
              <button data-testid="header-join-btn" onClick={() => navigate("/register")}
                className="rounded-full bg-yellow-400 hover:bg-yellow-300 text-yellow-950 font-bold px-4 py-2 text-sm transition-colors duration-200">
                Join Free
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero({ siteName }) {
  const navigate = useNavigate();
  return (
    <section data-testid="landing-hero" className="relative w-full overflow-hidden bg-[#02143f]">
      <img src={HERO_IMAGE} alt={`${siteName} — get real visitors to your website`} draggable="false"
        className="absolute inset-0 w-full h-full object-cover object-center select-none scale-[1.06]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#02143f]/90 via-[#02143f]/50 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-32">
        <div className="max-w-xl text-white">
          <div className="text-xs font-bold uppercase tracking-widest text-lime-300">Free Traffic Exchange</div>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]" style={HEADING_FONT}>
            Get <span className="text-yellow-300">REAL</span> Visitors to Your Website!
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/85">
            Surf other members' sites to earn credits, then spend them to send real visitors to yours.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button data-testid="hero-join-btn" onClick={() => navigate("/register")}
              className="inline-flex items-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 text-slate-900 font-black px-7 py-3.5 text-base shadow-lg hover:-translate-y-0.5 transition-transform duration-200">
              JOIN NOW — It's Free <ArrowRight size={18} weight="bold" />
            </button>
            <button data-testid="hero-login-btn" onClick={() => navigate("/login")}
              className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 hover:bg-white/20 text-white font-bold px-6 py-3.5 text-base backdrop-blur-md transition-colors duration-200">
              Member Login
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { user, siteSettings } = useAuth();
  useTrackVisit("/");

  const siteName = siteSettings?.site_name || "Start-A-TE";
  const logo = siteSettings?.logo_url || LOGO_URL;
  const googleReady = !!(siteSettings?.google_signin_enabled && siteSettings?.google_client_id);

  return (
    <div id="home" className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <Header logo={logo} user={user} googleReady={googleReady} clientId={siteSettings?.google_client_id} />
      <Hero siteName={siteName} />

    </div>
  );
}
